import React, {createRef, PureComponent, ReactNode} from "react"

import id from "@irati/utils/id"
import {FormattedMessage} from "@irati/utils/lang"

import theme from "../global/theme"
import {StyledButton, Wrapper} from "./styled/RadioFilter"
import {Radio} from "./"
import Flex from "../Flex"
import {Dropdown} from "../Dropdown"
import Button from "../Button"

interface FilterOption {
  label: string
  value: string
}

interface Props {
  value?: string
  onChange?: (value: string, option?: FilterOption) => void
  onReset?: () => void
  onFilter?: () => void
  pullRight?: boolean
  placeholder?: string
  toggleModalOnChange?: boolean
  children?: ReactNode
  footer?: boolean
  overflow?: boolean
  shouldFillIcon?: () => boolean
  onToggle?: () => void
  hideFooter?: boolean
  icon?: string
  rowRenderer?: () => void
  options: FilterOption[]
}

class RadioFilter extends PureComponent<Props> {
  dropdown = createRef<Dropdown>()

  static defaultProps: Omit<Props, "options"> = {
    onChange: () => null,
    onReset: () => {},
    onFilter: () => {},
    pullRight: false,
    toggleModalOnChange: true,
    footer: false,
    overflow: false,
    hideFooter: false
  }

  toggleModal = () => {
    return this.dropdown.current?.toggle()
  }

  shouldFillIcon = () => {
    const {shouldFillIcon, value} = this.props

    if (shouldFillIcon) {
      return shouldFillIcon() ? theme.colors.greys.mediumDark : undefined
    }
    return value ? theme.colors.greys.mediumDark : undefined
  }

  buildFooter = () => {
    const {hideFooter, onReset, onFilter, options, footer} = this.props
    const hasResults = options.length > 0

    if (hideFooter) {
      return null
    }
    if (onReset && hasResults) {
      if (footer) {
        return (
          <Flex justifyContent="space-between">
            <Button
              onClick={() => {
                onReset()
                this.toggleModal()
              }}>
              <FormattedMessage id="platform.radiofilter.resetbutton.text" />
            </Button>
            <Button
              primary
              onClick={() => {
                onFilter!()
                this.toggleModal()
              }}>
              <FormattedMessage id="platform.radiofilter.filterButton.text" />
            </Button>
          </Flex>
        )
      }
      return (
        <StyledButton
          onClick={() => {
            onReset()
            this.toggleModal()
          }}>
          <FormattedMessage id="platform.radiofilter.resetbutton.text" />
        </StyledButton>
      )
    }
    return null
  }

  handleChange = (optionValue: string, option: FilterOption) => {
    const {toggleModalOnChange, onChange} = this.props
    onChange!(optionValue, option)
    if (toggleModalOnChange) {
      this.toggleModal()
    }
  }

  render() {
    const {options, value, placeholder, children, overflow, onToggle, icon, rowRenderer} = this.props
    const idNamespace = id("RadioFilter", this.props)
    const hasResults = options.length > 0

    return (
      <Dropdown
        ref={this.dropdown}
        id={idNamespace}
        footer={this.buildFooter()}
        icon={icon}
        iconColor={this.shouldFillIcon()}
        width={250}
        onToggle={onToggle}>
        {!hasResults && !!placeholder && placeholder}
        {hasResults && (
          <Wrapper overflow={overflow}>
            <Radio rowRenderer={rowRenderer} id={idNamespace} name={name} value={value} onChange={this.handleChange} options={options} />
            {children}
          </Wrapper>
        )}
      </Dropdown>
    )
  }
}

export default RadioFilter
